import { cn } from '@/lib/utils';
import {
  getStatusVisual,
  getNotificationIconName,
  formatTimeAgo,
  NOTIFICATION_ICON_MAP,
} from '@/lib/notificationHelpers';
import type { INotificationCardCustomerProps } from '@/@types/notificationPanel';

function buildRefLine(notification: INotificationCardCustomerProps['notification']): string | null {
  const parts: string[] = [];

  if (notification.metadata?.claimNumber) {
    parts.push(`Claim ${notification.metadata.claimNumber}`);
  } else if (notification.metadata?.policyNumber) {
    parts.push(`Policy ${notification.metadata.policyNumber}`);
  } else if (notification.metadata?.preAuthNumber) {
    parts.push(`PA ${notification.metadata.preAuthNumber}`);
  } else if (notification.metadata?.invoiceNumber) {
    parts.push(`Invoice ${notification.metadata.invoiceNumber}`);
  }

  if (notification.metadata?.amount && notification.metadata?.currency) {
    parts.push(
      `${notification.metadata.currency} ${notification.metadata.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    );
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}

export function NotificationCardCustomer({
  notification,
  onClick,
}: INotificationCardCustomerProps) {
  const status = getStatusVisual(notification.type);
  const iconName = getNotificationIconName(notification.type);
  const IconComponent = NOTIFICATION_ICON_MAP[iconName];
  const isUnread = !notification.isRead;
  const refLine = buildRefLine(notification);

  return (
    <button
      type="button"
      onClick={() => onClick(notification._id, notification.metadata?.linkPath)}
      className={cn(
        'group w-full text-left flex items-stretch gap-0 transition-colors',
        'hover:bg-accent cursor-pointer',
        'border-b border-border'
      )}
    >
      {isUnread && <div className={cn('w-1 shrink-0 rounded-l', status.borderClass)} />}

      <div className={cn('flex-1 flex items-start gap-3 py-3.5 px-4', !isUnread && 'pl-5')}>
        <IconComponent
          className={cn(
            'w-5 h-5 shrink-0 mt-0.5',
            isUnread ? status.iconClass : 'text-muted-foreground/50'
          )}
        />

        <div className="flex-1 min-w-0 space-y-1">
          <p
            className={cn(
              'text-sm leading-tight transition-colors',
              isUnread
                ? 'font-semibold text-foreground'
                : 'font-medium text-muted-foreground group-hover:text-foreground'
            )}
          >
            {notification.title}
          </p>

          {refLine && (
            <p
              className={cn(
                'text-xs transition-colors',
                isUnread
                  ? 'text-muted-foreground'
                  : 'text-muted-foreground/70 group-hover:text-muted-foreground'
              )}
            >
              {refLine}
            </p>
          )}

          <p
            className={cn(
              'text-[13px] leading-relaxed transition-colors',
              isUnread
                ? 'text-muted-foreground'
                : 'text-muted-foreground/60 group-hover:text-muted-foreground'
            )}
          >
            {notification.message}
          </p>
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {formatTimeAgo(notification.createdAt)}
          </span>
          {isUnread && <span className={cn('w-2 h-2 rounded-full shrink-0', status.dotClass)} />}
        </div>
      </div>
    </button>
  );
}
